import { Box, Stack, Typography } from '@mui/material';
import { TipsAndUpdatesOutlined as BulbIcon } from '@mui/icons-material';
import type { TriggerType } from '@/types/dmAutomation';
import { ACCENT, ACCENT_TINT } from './_shared';

/** 배지(주체) 색 */
const BADGE_COLORS: Record<string, { color: string; bg: string }> = {
    나: { color: '#1976d2', bg: 'rgba(25,118,210,0.12)' },
    팔로워: { color: '#6a1b9a', bg: 'rgba(106,27,154,0.12)' },
    도우미: { color: ACCENT, bg: ACCENT_TINT },
};

interface Step {
    badge: string;
    text: string;
    example: string;
}

interface Explainer {
    title: string;
    steps: Step[];
    fit: string;
}

/** 앱(자동DM)의 "이렇게 동작해요" 카피 그대로 */
const EXPLAINERS: Record<TriggerType, Explainer> = {
    COMMENT_KEYWORD: {
        title: '피드 댓글+DM 자동화 — 이렇게 동작해요',
        steps: [
            { badge: '나', text: '내 게시물에 안내를 올려요', example: `"댓글에 '가이드' 적어주시면 자료 보내드려요 ✨"` },
            { badge: '팔로워', text: '내 게시물 댓글에 특정 키워드(단어)를 달아요', example: `"가이드", "쿠폰", "비타민 추천"` },
            { badge: '도우미', text: '댓글에 자동으로 답글을 달아줘요', example: `"DM 확인해주세요! 📬"` },
            {
                badge: '도우미',
                text: '동시에 그 사람에게 미리 만든 DM을 보내요',
                example: '미리 준비한 가이드 PDF · 쿠폰 코드 · 상담 예약 링크 등',
            },
        ],
        fit: '무료 자료 배포, 이벤트·쿠폰 신청, 영양제 추천 — 게시물 보고 관심 보인 사람을 정확히 잡고 싶을 때',
    },
    STORY_REPLY: {
        title: '스토리 댓글 자동화 — 이렇게 동작해요',
        steps: [
            {
                badge: '나',
                text: '스토리를 올리고 답장을 유도해요',
                example: `"이번 주 꿀팁 정리해뒀어요. 받고 싶은 분 답장 주세요 ✨"`,
            },
            { badge: '팔로워', text: '스토리에 답장을 보내요', example: '내용은 무엇이든 OK (이모지여도 동작)' },
            {
                badge: '도우미',
                text: '그 사람에게 미리 만든 DM을 자동으로 보내요',
                example: '미리 준비한 자료 · 쿠폰 · 매장/약국 위치 · 운영시간 등',
            },
        ],
        fit: '한정 자료 배포, 짧은 이벤트, 단골 팔로워와 친밀한 인터랙션을 만들 때 잘 맞아요',
    },
    DM_KEYWORD: {
        title: 'DM 답장 자동화 — 이렇게 동작해요',
        steps: [
            {
                badge: '나',
                text: '특정 단어(키워드) 와 답장 내용을 미리 만들어둬요',
                example: '단어 "가이드" → 가이드 PDF + 상담/예약 링크',
            },
            {
                badge: '팔로워',
                text: '내 DM 함에 그 단어가 들어간 메시지를 보내요',
                example: `"가이드 받고 싶어요", "비타민 복용법 알려주세요"`,
            },
            { badge: '도우미', text: '곧바로 자동 답장을 보내줘요', example: '준비된 자료 이미지 + 1:1 상담 버튼' },
        ],
        fit: 'FAQ, 가격 안내, 영양제 복용법 등 자주 들어오는 문의를 자동화하고 싶을 때 잘 맞아요',
    },
};

const Badge = ({ label }: { label: string }) => {
    const c = BADGE_COLORS[label] ?? BADGE_COLORS['도우미'];
    return (
        <Box
            component="span"
            sx={{
                flexShrink: 0,
                fontSize: 11.5,
                fontWeight: 700,
                px: 0.9,
                py: '2px',
                borderRadius: '6px',
                color: c.color,
                bgcolor: c.bg,
                lineHeight: 1.5,
            }}
        >
            {label}
        </Box>
    );
};

/** 선택한 트리거의 "이렇게 동작해요" 안내 카드 (앱과 동일 카피). */
const TriggerExplainer = ({ type }: { type: TriggerType }) => {
    const ex = EXPLAINERS[type];
    if (!ex) return null;

    return (
        <Box sx={{ mt: 2, p: { xs: 2, md: 2.5 }, borderRadius: 3, bgcolor: ACCENT_TINT }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.75 }}>
                <BulbIcon sx={{ fontSize: 20, color: ACCENT }} />
                <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: 'text.primary' }}>{ex.title}</Typography>
            </Stack>

            <Stack spacing={1.5}>
                {ex.steps.map((s, i) => (
                    <Stack key={i} direction="row" spacing={1.25} alignItems="flex-start">
                        <Box
                            sx={{
                                flexShrink: 0,
                                width: 22,
                                height: 22,
                                borderRadius: '50%',
                                bgcolor: ACCENT,
                                color: '#fff',
                                fontSize: 12,
                                fontWeight: 800,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mt: '1px',
                            }}
                        >
                            {i + 1}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                <Badge label={s.badge} />
                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}>
                                    {s.text}
                                </Typography>
                            </Stack>
                            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: '2px' }}>
                                예) {s.example}
                            </Typography>
                        </Box>
                    </Stack>
                ))}
            </Stack>

            <Box sx={{ mt: 2, p: 1.75, borderRadius: 2, bgcolor: '#fff', border: '1px solid rgba(0,0,0,0.06)' }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: ACCENT, mb: 0.5 }}>
                    이런 경우에 잘 맞아요
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.6 }}>{ex.fit}</Typography>
            </Box>
        </Box>
    );
};

export default TriggerExplainer;
